import { useCallback } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { Columns3, List } from 'lucide-react'
import { TaskPageLayout } from '../components/TaskPageLayout'
import UpcomingTaskList from '../components/UpcomingTaskList'
import { getUpcomingWindow, useUpcomingTasks } from '../hooks/useUpcomingTasks'
import type { TaskViewContext } from '../components/TaskPageLayout'
import type { TaskReorderEvent } from '../components/TaskList'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { useTaskPageSetup } from '@/hooks'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import useViewModeStore from '@/store/ViewModeStore'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useMobile'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/tasks/upcoming',
)({
  component: UpcomingPage,
})

/**
 * Helper function to extract due date timestamp from TaskDocument.
 * Handles various date formats and provides fallback for invalid dates.
 *
 * @param item - The task document
 * @returns Timestamp number or positive infinity for invalid/missing dates
 */
const getDueDateTimestamp = (item: TaskDocument) => {
  const value = item.dueDate
  if (!value) {
    return Number.POSITIVE_INFINITY
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? Number.POSITIVE_INFINITY : value
  }

  // Handle legacy string format if any
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed
  }

  return Number.POSITIVE_INFINITY
}

/**
 * Upcoming tasks page component with date-based organization and cross-date drag-and-drop.
 * 
 * Displays tasks due in the next 7 days, organized by date sections.
 * Features advanced drag-and-drop that allows moving tasks between different dates
 * while preserving time and updating due dates automatically.
 * 
 * Features:
 * - Date-sectioned task organization (grouped by due date)
 * - Cross-date drag-and-drop with automatic due date updates
 * - Time preservation when moving between dates
 * - Smart sorting by due date and sort order
 * - Complex reordering logic for multi-date scenarios
 * 
 * Route: `/$workspaceSlug/tasks/upcoming`
 * 
 * @example
 * ```tsx
 * // Accessed via URL like: /my-workspace/tasks/upcoming
 * // Shows tasks for next 7 days grouped by date
 * // Supports dragging tasks between different date sections
 * ```
 */
function UpcomingPage() {
  useTaskPageSetup()
  const { t } = useTranslation('tasks')
  // The Week board needs seven columns; phones always get the list (design 36).
  const isPhone = useIsMobile()
  const savedLayout = useViewModeStore((s) => s.upcomingLayout)
  const layout = isPhone ? 'list' : savedLayout
  const setLayout = useViewModeStore((s) => s.setUpcomingLayout)

  const now = DateTime.now()
  const startOfTomorrow = now.plus({ days: 1 }).startOf('day').toJSDate()

  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const { tasks, isLoading } = useUpcomingTasks({ includeSubTasks: showSubTasks })
  const { updateTask } = useTaskMutations()

  const transformUpcomingItems = useCallback(
    (items: Array<TaskDocument>) =>
      [...items].sort((a, b) => {
        const aDue = getDueDateTimestamp(a)
        const bDue = getDueDateTimestamp(b)

        if (aDue !== bDue) {
          return aDue - bDue
        }

        return compareRanks(a.sortOrder, b.sortOrder)
      }),
    [],
  )
  const handleReorderWithDateUpdate = useCallback(
    (
      event: TaskReorderEvent,
      originalOnReorder?: (event: TaskReorderEvent) => void,
      allItems?: Array<TaskDocument>,
    ) => {
      // Check if task was moved between different date sections
      const isMovingBetweenSections =
        event.sourceSectionId &&
        event.targetSectionId &&
        event.sourceSectionId !== event.targetSectionId

      if (isMovingBetweenSections) {
        // Parse the target section ID (which is an ISO date string like "2025-10-23")
        const targetDate = DateTime.fromISO(event.targetSectionId!)

        if (!targetDate.isValid) {
          return
        }

        // Get the source task to preserve its time
        const sourceItem = allItems?.find((item) => item.id === event.sourceId)
        if (!sourceItem) {
          return
        }

        // Parse existing dueDate to preserve time
        let existingTime: { hour: number; minute: number } = {
          hour: 0,
          minute: 0,
        }
        if (sourceItem.dueDate) {
          const existingDateTime =
            typeof sourceItem.dueDate === 'number'
              ? DateTime.fromMillis(sourceItem.dueDate)
              : DateTime.fromISO(sourceItem.dueDate)

          if (existingDateTime.isValid) {
            existingTime = {
              hour: existingDateTime.hour,
              minute: existingDateTime.minute,
            }
          }
        }

        // Create new dueDate with target date but preserve time
        const newDueDate = targetDate.set(existingTime).toJSDate()
        // A range moves as a whole: the start shifts by the same number of days.
        const newStartDate =
          sourceItem.startDate && sourceItem.dueDate
            ? sourceItem.startDate + (newDueDate.getTime() - sourceItem.dueDate)
            : sourceItem.startDate

        // Get all items in the target date section (after the dueDate change would happen)
        const targetDateStart = targetDate.startOf('day')
        const targetDateEnd = targetDate.endOf('day')

        const itemsInTargetSection = (allItems || [])
          .filter((item) => {
            if (item.id === event.sourceId) {
              return false // Exclude the moving item
            }
            if (!item.dueDate) {
              return false
            }
            const itemDate =
              typeof item.dueDate === 'number'
                ? DateTime.fromMillis(item.dueDate)
                : DateTime.fromISO(item.dueDate)
            return (
              itemDate.isValid &&
              itemDate >= targetDateStart &&
              itemDate <= targetDateEnd
            )
          })
          .sort((a, b) => compareRanks(a.sortOrder, b.sortOrder))

        // Calculate the new sortOrder based on drop position
        let beforeSort: string | null = null
        let afterSort: string | null = null

        if (event.type === 'item') {
          // Find the target item's position in the target section
          const targetIndex = itemsInTargetSection.findIndex(
            (item) => item.id === event.targetId,
          )

          if (targetIndex !== -1) {
            if (event.dropPosition === 'before') {
              // Insert before the target
              beforeSort =
                targetIndex > 0
                  ? (itemsInTargetSection[targetIndex - 1]?.sortOrder ?? null)
                  : null
              afterSort = itemsInTargetSection[targetIndex]?.sortOrder ?? null
            } else {
              // Insert after the target
              beforeSort = itemsInTargetSection[targetIndex]?.sortOrder ?? null
              afterSort =
                targetIndex < itemsInTargetSection.length - 1
                  ? (itemsInTargetSection[targetIndex + 1]?.sortOrder ?? null)
                  : null
            }
          }
        } else {
          // Drop at the end of the target section
          beforeSort =
            itemsInTargetSection.length > 0
              ? (itemsInTargetSection[itemsInTargetSection.length - 1]
                  ?.sortOrder ?? null)
              : null
          afterSort = null
        }

        const newSortOrder = generateRankBetween(beforeSort, afterSort)

        // Update both dueDate and sortOrder
        try {
          void updateTask(event.sourceId, {
            dueDate: newDueDate.getTime(),
            startDate: newStartDate ?? null,
            sortOrder: newSortOrder,
          })
        } catch (error) {
          // Error handling
        }
      } else {
        // Same section, just use the original reorder handler
        originalOnReorder?.(event)
      }
    },
    [updateTask],
  )

  const renderUpcomingTaskList = useCallback(
    (context: TaskViewContext) => {
      // Wrap the original onReorder to add date update logic
      const wrappedOnReorder = context.defaultOnReorder
        ? (event: TaskReorderEvent) =>
            handleReorderWithDateUpdate(
              event,
              context.defaultOnReorder,
              context.items,
            )
        : undefined

      return <UpcomingTaskList {...context} onReorder={wrappedOnReorder} layout={layout} />
    },
    [handleReorderWithDateUpdate, layout],
  )

  const overdueCount = tasks.filter((task) => !!task.dueDate && task.dueDate < getUpcomingWindow().today).length
  const headerCount = overdueCount > 0
    ? `${tasks.length} · ${t('upcoming.overdueCount', { count: overdueCount })}`
    : tasks.length

  const layoutSwitch = (
    <div role="group" aria-label={t('upcoming.layoutLabel')} className="flex h-8 items-center rounded-lg bg-secondary p-0.5">
      {(['list', 'week'] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={layout === option}
          onClick={() => setLayout(option)}
          className={cn(
            'flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground',
            layout === option && 'bg-card text-foreground shadow-sm',
          )}
        >
          {option === 'list' ? <List className="size-3.5" aria-hidden /> : <Columns3 className={cn('size-3.5', layout === option && 'text-brand')} aria-hidden />}
          {option === 'list' ? t('upcoming.layoutList') : t('upcoming.layoutWeek')}
        </button>
      ))}
    </div>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <TaskPageLayout
        tasks={tasks}
        isLoading={isLoading}
        transformItems={transformUpcomingItems}
        defaultDueDate={startOfTomorrow}
        renderTaskView={renderUpcomingTaskList}
        showAddTaskInput={false}
        pageTitle={t('upcoming.title')}
        headerCount={headerCount}
        headerActions={isPhone ? undefined : layoutSwitch}
        wide={layout === 'week'}
        navigateToSubtasks={true}
      />
    </div>
  )
}

export default UpcomingPage
